import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Search, Building2, Users } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import { Card, CardContent } from '../../../../components/ui/card';
import AdminLayout from '../../../../layouts/AdminLayout';
import { cn } from '../../../../lib/utils';
import apiService from '../../../../services/api.service';
import DepartmentsTable from './components/DepartmentsTable';

function AdminDepartmentsPageV3({ title }) {
  const [departmentsData, setDepartmentsData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('employeesCount');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchDepartments = useCallback(async () => {
    try {
      setIsLoading(true);
      const sort = sortOrder === 'asc' ? sortField : `-${sortField}`;
      const response = await apiService.admin.getDepartments({
        params: {
          search: searchValue,
          skip: currentPage * rowsPerPage,
          limit: rowsPerPage,
          sort,
        },
      });

      setDepartmentsData(response.paginatedResult);
      setTotalCount(response.totalCount);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, rowsPerPage, searchValue, sortField, sortOrder]);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const debouncedSearch = useDebouncedCallback(() => {
    setCurrentPage(0);
  }, 300);

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    debouncedSearch();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(0);
  };

  // Calculate stats
  const totalEmployees = departmentsData.reduce((acc, dept) => acc + dept.employeesCount, 0);

  return (
    <AdminLayout title={title}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">{title}</h1>
              <p className="text-muted-foreground text-sm">
                Manage departments and assign department heads
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search departments..."
              value={searchValue}
              onChange={handleSearchChange}
              className={cn(
                'w-64 pl-9 pr-4 py-2 rounded-lg text-sm',
                'bg-background border border-input',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
                'placeholder:text-muted-foreground',
              )}
            />
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Building2 className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Departments</p>
                  <p className="text-2xl font-bold">{totalCount}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Users className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Employees</p>
                  <p className="text-2xl font-bold">{totalEmployees}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card style={{ border: '1px solid hsl(var(--border))' }}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Users className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Avg. per Department</p>
                  <p className="text-2xl font-bold">
                    {totalCount > 0 ? Math.round(totalEmployees / totalCount) : 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Departments Table */}
        <Card style={{ border: '1px solid hsl(var(--border))' }}>
          <CardContent className="p-0">
            <DepartmentsTable
              departmentsData={departmentsData}
              isLoading={isLoading}
              totalCount={totalCount}
              currentPage={currentPage}
              rowsPerPage={rowsPerPage}
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onRefresh={fetchDepartments}
            />
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}

AdminDepartmentsPageV3.propTypes = {
  title: PropTypes.string.isRequired,
};

export default AdminDepartmentsPageV3;
